// src/utils/dateUtils.ts
import dayjs, { Dayjs } from 'dayjs';

type DateInput = Date | string | null | undefined;

/**
 * Normalizes a date to ISO string format for storage.
 * Handles Date objects, strings, and null/undefined.
 * BACKWARD COMPATIBLE: Works with both old Date objects and new ISO strings.
 */
export function normalizeDateForStorage(date: DateInput): string | null {
	if (!date) return null;

	// If already a string, validate it's parseable then return normalized
	if (typeof date === 'string') {
		const parsed = dayjs(date);
		return parsed.isValid() ? parsed.toISOString() : null;
	}

	// If Date object, convert to ISO string
	if (date instanceof Date) {
		return dayjs(date).toISOString();
	}

	return null;
}

/**
 * Parses a stored date into a dayjs object.
 * BACKWARD COMPATIBLE: Works with both Date objects and ISO strings from localStorage.
 */
export function parseStoredDate(date: DateInput): Dayjs | null {
	if (!date) return null;
	const parsed = dayjs(date);
	return parsed.isValid() ? parsed : null;
}

/**
 * Parses a stored date into a native Date object (for Mantine DatePicker compatibility).
 */
export function parseStoredDateAsNative(date: DateInput): Date | null {
	if (!date) return null;
	const parsed = dayjs(date);
	return parsed.isValid() ? parsed.toDate() : null;
}

/**
 * Formats a date for display.
 */
export function formatDateForDisplay(
	date: DateInput,
	format: string = 'M/D'
): string {
	if (!date) return '';
	const parsed = dayjs(date);
	return parsed.isValid() ? parsed.format(format) : '';
}

/**
 * Checks if a date is before today (overdue).
 * Uses day-level comparison to avoid time-of-day issues.
 */
export function isDateOverdue(date: DateInput): boolean {
	if (!date) return false;
	const parsed = dayjs(date);
	return parsed.isValid() && parsed.isBefore(dayjs(), 'day');
}
